import type { ReactNode } from 'react';
import type { Bulle, Decompte, Maillon, Priorite } from '../partage/api/types';
import { gestesDuCentre, gestesDuToucher } from '../partage/deuxGestes';
import { useAuPouce } from '../partage/surface';
import { HautDuPouce } from '../partage/HautDuPouce';
import { Curseur, Interrupteur, type Cran } from '../partage/PiecesDuCarnet';
import {
  ANALYSE_COUCHEE,
  BARRE_A_SA_PLACE,
  GESTE_DU_CENTRE,
  HIERARCHIE_COUCHEE,
  LES_VUES_COUCHEES,
  MES_PROJETS,
  MONTRER_CE_QUI_EST_FAIT,
  PROJET_EN_TETE,
  PROJETS,
  REGLER_LE_PROJET,
  RENOMMER_COULEUR_DU_PROJET,
  surN,
  TITRE_AU_POUCE,
} from '../partage/vocabulaire';
import { BasculeVue, type Vue } from './BasculeVue';
import { ParametresDeFeuille } from './ParametresDeFeuille';
import type { VueCouchee } from './vueCouchee';
import './TeteDeFeuille.css';

/**
 * Où tu es, et par où tu remontes.
 *
 * La tête d'une feuille ne décore pas : elle répond à la seule question qu'on se pose en
 * arrivant sur une surface emboîtée — **d'où je viens, et qu'est-ce que je regarde**. Sans
 * le fil, on descend de trois crans et l'app devient un labyrinthe ; c'est exactement ce qui
 * rend les arbres inutilisables ailleurs.
 *
 * Elle porte aussi la bascule entre les vues, le geste qui appartient à la feuille entière, pas
 * à une bulle. « Hiérarchiser » n'a plus de bouton visible : la touche h le lance (chantier
 * `tete-du-projet`). « Sa fiche » est parti (ticket 13 de retours-testeurs) : le double-clic
 * ouvre la fiche du centre, sur la bulle du centre comme sur le titre.
 *
 * Au téléphone (ticket 05 du chantier telephone), le haut du carnet (chantier telephone-habit,
 * 02, `HautDuPouce`, maquette `projet()`) : le retour dans la marge (vers la mère, ou « Mes
 * projets »), au-dessus du titre le fil (« projet · X sur Y » à la racine), le titre, qui porte ☰
 * et ouvre sa fiche d'un toucher, et le ⚙ « Régler le projet » : « Montrer ce qui est fait » et
 * « Renommer, couleur du projet › », qui mène à la fiche du projet (chantier telephone-projet,
 * 02). Rien dessous : l'arbre du projet commence (`ArbreDuProjet`). Ni onglets de vues.
 *
 * Sur l'ordinateur (chantier `tete-du-projet`, modification 01, maquette du carnet
 * `projet-ouvert.html`, `.tete`, `.angles` et `.outils`) : **épurée**, sur une ligne, « ‹ Projets »
 * (ou la mère) et le titre à gauche, les angles entre les deux, puis, tout à droite, les outils
 * du projet que la feuille donne (`outils` : « Profondeur 1 », « Habit », retour d'Igor du
 * 2026-10-20) ; plus de compte « N sur M · au plus tôt le … » (décision 1 d'Igor). Trop étroit,
 * les outils passent dessous, à droite. La légende de la carte est dans l'ampoule de l'Aide
 * (modification 02).
 *
 * Couché, dans un projet ou une branche (`couche`, chantier telephone-couche, 01, maquette
 * `cProjet()`, `tetec()`) : le haut du carnet sur une ligne, le retour, la pastille de la couleur,
 * le titre de la feuille, le curseur Analyse / Hiérarchie de 220 px, et le même ⚙ qu'au pouce.
 */
export function TeteDeFeuille({
  bulle,
  chemin,
  parent,
  decompte,
  avecParametres,
  vue,
  onVue,
  hierarchisable,
  onHierarchiser,
  calque,
  onRemonter,
  onMaillon,
  onSaFiche,
  outils = null,
  montrerCeQuiEstFait = true,
  onMontrerCeQuiEstFait,
  projet = null,
  couche = null,
}: {
  bulle: Bulle;
  chemin: Maillon[];
  parent: Maillon | null;
  /** « N sur M » de la feuille, compté par le serveur avec la feuille (chantier nettoyage-gestes, ticket 04). */
  decompte: Decompte;
  /** Le ⚙ : en Hiérarchie, sur l'ordinateur ; jamais en Liste ni au téléphone. */
  avecParametres: boolean;
  vue: Vue;
  onVue: (vue: Vue) => void;
  /** Rien à ordonner sous deux bulles, et rien à proposer pendant qu'on ordonne déjà. */
  hierarchisable: boolean;
  onHierarchiser: () => void;
  /** Le calque de priorité, rangé dans ⚙ (ticket 04 du chantier `priorites`) ; absent en Liste. */
  calque?: { enAvant: Priorite | null; choisir: (enAvant: Priorite | null) => void };
  onRemonter: () => void;
  onMaillon: (id: string) => void;
  /** Le double-clic sur le titre : la fiche du centre, dans toutes les vues. */
  onSaFiche: () => void;
  /** Les outils du projet, à droite des angles, à l'ordinateur : « Profondeur 1 », « Habit ». */
  outils?: ReactNode;
  /** Au téléphone, « Montrer ce qui est fait » dans le ⚙ (chantier telephone-projet, 02). */
  montrerCeQuiEstFait?: boolean;
  onMontrerCeQuiEstFait?: (oui: boolean) => void;
  /**
   * Au téléphone, sous le projet (chantier telephone-projet, 03) : le haut reste celui du projet,
   * son titre, son « N sur M » (`null` tant qu'il n'est pas lu) et sa fiche ; le fil passe dans la page.
   */
  projet?: { titre: string; decompte: Decompte | null; onSaFiche: () => void } | null;
  /** Le téléphone couché : la vue choisie, Analyse ou Hiérarchie. */
  couche?: { vue: VueCouchee; onVue: (vue: VueCouchee) => void } | null;
}) {
  const auPouce = useAuPouce();
  const racine = parent === null;

  const vues = (
    <>
      <BasculeVue vue={vue} onBasculer={onVue} />
      {/* Le ⚙ de la Hiérarchie (ticket 13 de retours-testeurs) porte « Hiérarchiser » et le calque
          d'importance : caché, pas supprimé (chantier tete-du-projet, décisions 5 et 6 d'Igor). La
          touche h reste ; le calque pourra resservir. */}
      {avecParametres && (
        <div className="feuille-parametres-caches" hidden>
          <ParametresDeFeuille
            hierarchisable={hierarchisable}
            onHierarchiser={onHierarchiser}
            calque={calque}
          />
        </div>
      )}
    </>
  );

  const remonter = (
    <button type="button" className="feuille-remonter" data-tuto="fil" onClick={onRemonter}>
      <svg width="16" height="16" viewBox="0 0 17 17" fill="none" aria-hidden="true">
        <path d="M10.6 3.2L5.4 8.5l5.2 5.3" stroke="currentColor" strokeWidth="1.5" />
      </svg>
      {/* Le marqueur d'un dossier est un POINT ROND, jamais une barre (AGENTS). */}
      <span className="feuille-fil-point" aria-hidden="true" />
      {parent ? parent.titre : auPouce ? MES_PROJETS : PROJETS}
    </button>
  );
  const chemins = chemin.length > 2 && (
    <span className="feuille-chemin">
      {chemin.slice(0, -2).map((maillon) => (
        <button key={maillon.id} type="button" data-tuto="fil" onClick={() => onMaillon(maillon.id)}>
          {maillon.titre}
        </button>
      ))}
    </span>
  );
  /* Le centre, où l'on est déjà : un double-clic ouvre sa fiche, comme sur la bulle du centre
     de la carte. C'est le seul chemin en Liste, qui n'a pas de bulle du centre. */
  const titre = (
    <h1>
      <button type="button" className="feuille-titre" {...gestesDuCentre(onSaFiche)} title={GESTE_DU_CENTRE}>
        {bulle.titre}
      </button>
    </h1>
  );
  /*
   * Au téléphone (chantier `telephone-cadre`, ticket 04, cadrage 4) : le titre porte ☰ et un
   * toucher ouvre la fiche du projet ; l'appui long aussi. Plus bas que le projet, le haut reste
   * celui du projet (chantier telephone-projet, 03, maquette `projet()`).
   */
  const enHaut = !racine && projet ? projet : { titre: bulle.titre, decompte, onSaFiche };
  const titreAuPouce = (
    <button
      type="button"
      className="feuille-titre"
      {...gestesDuToucher(enHaut.onSaFiche, enHaut.onSaFiche)}
      title={TITRE_AU_POUCE}
    >
      {enHaut.titre}
      <span className="feuille-titre-fiche" aria-hidden="true">
        {' '}☰
      </span>
    </button>
  );

  const reglages = {
    titre: REGLER_LE_PROJET,
    contenu: (fermer: () => void) => (
      <>
        {onMontrerCeQuiEstFait && (
          <Interrupteur
            titre={MONTRER_CE_QUI_EST_FAIT}
            sous={BARRE_A_SA_PLACE}
            oui={montrerCeQuiEstFait}
            onBasculer={onMontrerCeQuiEstFait}
          />
        )}
        <button
          type="button"
          className="pouce-ligne-reg feuille-reglage-fiche"
          onClick={() => {
            fermer();
            enHaut.onSaFiche();
          }}
        >
          {RENOMMER_COULEUR_DU_PROJET}
          <span aria-hidden="true">›</span>
        </button>
      </>
    ),
  };
  const retour = { nom: parent ? parent.titre : MES_PROJETS, onRetour: onRemonter, tuto: 'fil' };

  if (couche) {
    return (
      <HautDuPouce
        couche
        classe="haut-pouce--projet-couche"
        titre={
          <>
            <i className="feuille-pastille-couchee" aria-hidden="true" />
            <span className="feuille-titre-couche">{bulle.titre}</span>
          </>
        }
        apres={
          <span className="feuille-vues-couchees">
            <Curseur
              mini
              largeur={220}
              nom={LES_VUES_COUCHEES}
              crans={VUES_COUCHEES}
              choisi={couche.vue}
              onChoisir={couche.onVue}
              attribut="vue-couchee"
            />
          </span>
        }
        retour={retour}
        reglages={reglages}
      />
    );
  }

  /*
   * L'ordinateur : une seule ligne, comme les maquettes. Le fil (la classe `feuille-fil`) porte
   * aussi le titre, « N sur M · au plus tôt le … » et les vues.
   */
  if (!auPouce) {
    return (
      <header className="feuille-fil feuille-ligne" aria-label="où tu es">
        <div className="feuille-ligne-ou">
          {/* À la racine d'un projet, pas de « ‹ Projets » : la barre de gauche et la page Projets
              y mènent déjà (retour d'Igor du 2026-10-20). Plus bas, il ramène à la mère. */}
          {!racine && remonter}
          {chemins}
          {titre}
        </div>
        <div className="feuille-ligne-angles">{vues}</div>
        {outils && <div className="feuille-ligne-outils">{outils}</div>}
      </header>
    );
  }

  const dessus = (
    <span className="feuille-dessus-projet">
      <span className="feuille-fil-point" aria-hidden="true" />
      {PROJET_EN_TETE}
      {enHaut.decompte && <> · {surN(enHaut.decompte.faites, enHaut.decompte.total)}</>}
    </span>
  );

  return (
    <>
      <HautDuPouce
        classe="haut-pouce--projet"
        titre={titreAuPouce}
        dessus={dessus}
        retour={retour}
        reglages={reglages}
      />
    </>
  );
}

/** Les deux crans du curseur couché, dans l'ordre de la maquette. */
const VUES_COUCHEES: readonly Cran<VueCouchee>[] = [
  ['analyse', ANALYSE_COUCHEE],
  ['hierarchie', HIERARCHIE_COUCHEE],
];
